import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import React, { useCallback, useMemo, useState } from "react";

import { CapabilityClaim, ClaimPart, EntityRef } from "../../types/productPlane";
import EntityLink from "./EntityLink";
import Toggle, { TOGGLE_WIDTH } from "./RowToggle";
import SourcesChip from "./SourcesChip";
import {
  Beneath,
  ClaimPartName,
  ClaimParts as Parts,
  NONE,
  StanceChip,
  columnHeader,
  quiet,
  tableFrame,
} from "./claimCells";
import {
  ClaimBand,
  ClaimGroup,
  CountedPart,
  CountedWords,
  countedClaims,
  groupClaims,
  groupName,
  statedMaturity,
} from "./claimGroups";
import { codeWords } from "./format";

export interface ClaimTableProps {
  // The claims of one bearer, in any order
  claims: CapabilityClaim[];
  // A plain table for a page that is printed: every row listed and none
  // opened on its claims, no control, no sources column
  print?: boolean;
}

const COLUMNS = [
  "Stance",
  "Ability",
  "Condition",
  "Mode",
  "Grade",
  "Maturity",
  "Sources",
];

// The columns of the printed table: all but the sources
const PRINT_COLUMNS = COLUMNS.slice(0, -1);

// The width (px) below which the table scrolls sideways: under the width of
// the overlay, so that the sources are in view there
const TABLE_MIN_WIDTH = 600;

// Most names the line of the unlisted abilities spells out
const NAMES_SHOWN = 4;

// What a claim states of itself beyond the columns of its group: the claim
// in a sentence, and the means
const hasMeans = (claim: CapabilityClaim): boolean =>
  claim.capability.means.length > 0 || claim.means_product !== null;

const hasMore = (claim: CapabilityClaim): boolean =>
  Boolean(claim.summary) || hasMeans(claim);

// The stance, a part, a part on no curated list and an empty cell are drawn
// by ./claimCells, which the bearer table of a capability part uses too

// How many of a group's claims state a value, where more than one does
const Times: React.FC<{ count: number; of: number }> = ({ count, of }) =>
  count > 1 ? (
    <>
      {/* A space: in a narrow column the number goes beneath its value */}{" "}
      <Box
        component="span"
        title={`In ${count} of the ${countedClaims(of)}`}
        sx={{ ...quiet, fontSize: "0.7rem", whiteSpace: "nowrap" }}
      >
        ×{count}
      </Box>
    </>
  ) : null;

// The parts of one role over a group's claims, one beneath the other
const CountedParts: React.FC<{ parts: CountedPart[]; of: number }> = ({
  parts,
  of,
}) => (
  <>
    {parts.map(({ part, count }) => (
      <Box key={part.part.id}>
        <ClaimPartName part={part} />
        <Times count={count} of={of} />
      </Box>
    ))}
  </>
);

// Most letters of one word that is kept in one line
const WORD_KEPT = 10;

// A short word is not broken at its hyphen ("long-range"); a longer one
// and a phrase wrap
const gradeWords = (words: string) =>
  ({
    whiteSpace:
      /\s/.test(words.trim()) || words.trim().length > WORD_KEPT
        ? "normal"
        : "nowrap",
  }) as const;

// How well or how precisely, in the documents' words
const Grades: React.FC<{ grades: CountedWords[]; of: number }> = ({
  grades,
  of,
}) => (
  <>
    {grades.map(({ words, count }) => (
      <Box key={words} sx={gradeWords(words)}>
        {words}
        <Times count={count} of={of} />
      </Box>
    ))}
  </>
);

// The maturity cell: its words in one quiet line, or a dash when it has none
const MaturityCell: React.FC<{ words: (string | null)[] }> = ({ words }) => {
  const stated = words.filter((text): text is string => Boolean(text));
  return (
    <TableCell sx={{ ...quiet, fontSize: "0.75rem" }}>
      {stated.length === 0
        ? NONE
        : stated.map((text, index) => (
            <React.Fragment key={`${index}-${text}`}>
              {index > 0 && " · "}
              <span>{text}</span>
            </React.Fragment>
          ))}
    </TableCell>
  );
};


// The rows beneath an open group are set back on a faint tint
const detailRow = {
  verticalAlign: "top",
  backgroundColor: (theme: Theme) => alpha(theme.palette.common.white, 0.02),
} as const;

const detailText = { ...quiet, fontSize: "0.75rem" } as const;

// The means of a claim in one quiet line, and the product that provides it
const Means: React.FC<{ claim: CapabilityClaim }> = ({ claim }) => {
  const { means } = claim.capability;
  return (
    <Box sx={detailText}>
      via{" "}
      {means.map((part: ClaimPart, index) => (
        <React.Fragment key={part.part.id}>
          {index > 0 && ", "}
          <ClaimPartName part={part} quieter />
        </React.Fragment>
      ))}
      {claim.means_product && (
        <>
          {means.length > 0 && ", "}
          <EntityLink
            id={claim.means_product.id}
            type={claim.means_product.type}
            name={claim.means_product.name}
            sx={{ fontSize: "0.75rem" }}
          />
        </>
      )}
    </Box>
  );
};

// One claim beneath its open group: the claim in a sentence with its means,
// then what it states itself, under the columns. A group of one claim
// states the same in its own row, so only the sentence and the means are
// left to show.
const ClaimDetail: React.FC<{ claim: CapabilityClaim; alone: boolean }> = ({
  claim,
  alone,
}) => {
  const { capability } = claim;
  const values = !alone;
  return (
    <>
      {hasMore(claim) && (
        <TableRow data-testid="claim-sentence" sx={detailRow}>
          <TableCell sx={values ? { borderBottom: 0 } : undefined} />
          <TableCell
            colSpan={COLUMNS.length - 1}
            sx={{ ...detailText, ...(values ? { borderBottom: 0, pb: 0 } : {}) }}
          >
            {claim.summary}
            {hasMeans(claim) && <Means claim={claim} />}
          </TableCell>
        </TableRow>
      )}
      {values && (
        <TableRow data-testid="claim-detail" sx={detailRow}>
          <TableCell colSpan={2} />
          <TableCell>
            <Parts parts={capability.conditions} />
            {claim.conditions_wording && (
              <Beneath>{claim.conditions_wording}</Beneath>
            )}
          </TableCell>
          <TableCell>
            <Parts parts={capability.modes} />
          </TableCell>
          <TableCell>
            {claim.grade_words.map((words) => (
              <Box key={words} sx={gradeWords(words)}>
                {words}
              </Box>
            ))}
          </TableCell>
          <MaturityCell
            words={[
              statedMaturity(claim) && codeWords(statedMaturity(claim)),
              claim.context,
            ]}
          />
          <TableCell>
            <SourcesChip sources={claim.sources} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface GroupRowsProps {
  group: ClaimGroup;
  open: boolean;
  onToggle: (key: string) => void;
  print?: boolean;
}

// The claims of one stance on one ability in a row: what they state, each
// value with the claims that state it. The row opens on its claims, except
// on a printed page.
const GroupRows: React.FC<GroupRowsProps> = ({
  group,
  open: openAsked,
  onToggle,
  print = false,
}) => {
  const { claims } = group;
  const alone = claims.length === 1;
  const [first] = claims;
  const opens = !print && (!alone || hasMore(first));
  const open = openAsked && !print;
  return (
    <>
      <TableRow data-testid="claim-group" sx={{ verticalAlign: "top" }}>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
            {opens ? (
              <Toggle
                open={open}
                label={`Claims: ${group.stance} ${groupName(group)}`}
                onToggle={() => onToggle(group.key)}
              />
            ) : (
              <Box sx={{ width: TOGGLE_WIDTH, flexShrink: 0 }} />
            )}
            <StanceChip stance={group.stance} />
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ fontWeight: 500 }}>
            <Parts parts={group.abilities} />
          </Box>
          <Parts parts={group.objects} quieter />
          {!alone && <Beneath>{countedClaims(claims.length)}</Beneath>}
        </TableCell>
        <TableCell>
          <CountedParts parts={group.conditions} of={claims.length} />
          {alone && first.conditions_wording && (
            <Beneath>{first.conditions_wording}</Beneath>
          )}
        </TableCell>
        <TableCell>
          <CountedParts parts={group.modes} of={claims.length} />
        </TableCell>
        <TableCell>
          <Grades grades={group.grades} of={claims.length} />
        </TableCell>
        <MaturityCell
          words={[
            group.maturity && codeWords(group.maturity),
            alone ? first.context : null,
          ]}
        />
        {!print && (
          <TableCell>
            <SourcesChip sources={alone ? first.sources : group.sources} />
          </TableCell>
        )}
      </TableRow>
      {open &&
        claims.map((claim, index) => (
          <ClaimDetail
            key={`${claim.capability.capability.id}-${index}`}
            claim={claim}
            alone={alone}
          />
        ))}
    </>
  );
};

// What the line of a band's unlisted abilities reads: how many, and the
// first of them by name
export const unlistedLine = (groups: ClaimGroup[]): string => {
  const names = groups.slice(0, NAMES_SHOWN).map(groupName);
  const rest = groups.length - names.length;
  return `${groups.length} not on the backbone: ${names.join(", ")}${
    rest > 0 ? `, +${rest} more` : ""
  }`;
};

interface UnlistedRowsProps {
  band: ClaimBand;
  isOpen: (key: string) => boolean;
  onToggle: (key: string) => void;
  print?: boolean;
}

// The abilities of a band that are on no curated list, in one line that
// opens on their rows; on a printed page the line is a heading and the rows
// follow it
const UnlistedRows: React.FC<UnlistedRowsProps> = ({
  band,
  isOpen,
  onToggle,
  print = false,
}) => {
  const key = `${band.key}#unlisted`;
  const open = isOpen(key) || print;
  return (
    <>
      <TableRow data-testid="claims-unlisted">
        <TableCell
          colSpan={(print ? PRINT_COLUMNS : COLUMNS).length}
          sx={{ py: "5px", ...(print ? { ...quiet, fontSize: "0.8125rem" } : {}) }}
        >
          {print ? (
            unlistedLine(band.unlisted)
          ) : (
          <ButtonBase
            aria-expanded={open}
            onClick={() => onToggle(key)}
            sx={{
              ...quiet,
              font: "inherit",
              fontSize: "0.8125rem",
              textAlign: "left",
              alignItems: "flex-start",
              gap: "2px",
              borderRadius: "4px",
              "&:hover": { color: "text.primary" },
              "&.Mui-focusVisible": { outline: "2px solid", outlineOffset: 2 },
            }}
          >
            <ChevronRightIcon
              sx={{
                fontSize: 18,
                width: TOGGLE_WIDTH,
                flexShrink: 0,
                transform: open ? "rotate(90deg)" : "none",
                transition: "transform 120ms",
              }}
            />
            <span>{unlistedLine(band.unlisted)}</span>
          </ButtonBase>
          )}
        </TableCell>
      </TableRow>
      {open &&
        band.unlisted.map((group) => (
          <GroupRows
            key={group.key}
            group={group}
            open={isOpen(group.key)}
            onToggle={onToggle}
            print={print}
          />
        ))}
    </>
  );
};

// An area in a band: a link to its part view where it has one, in the
// band's own type either way
const AreaName: React.FC<{ area: EntityRef }> = ({ area }) =>
  area.type === "capability_part" ? (
    <EntityLink
      id={area.id}
      type={area.type}
      name={area.name}
      sx={{ color: "inherit", textDecorationColor: "currentColor" }}
    />
  ) : (
    <>{area.name}</>
  );

// The band rows: small capitals on a faint tint, above the claims they name
const bandCell = {
  ...quiet,
  backgroundColor: (theme: Theme) => alpha(theme.palette.chart.ink, 0.03),
  fontSize: "0.6875rem",
  fontWeight: 500,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  py: "5px",
} as const;

// What a band reads: its own label, else its areas
const BandName: React.FC<{ band: ClaimBand }> = ({ band }) => {
  if (band.label) {
    return <>{band.label}</>;
  }
  if (!band.effectArea && !band.functionArea) {
    return <>Other</>;
  }
  return (
    <>
      {band.effectArea && <AreaName area={band.effectArea} />}
      {band.effectArea && band.functionArea && " › "}
      {band.functionArea && <AreaName area={band.functionArea} />}
    </>
  );
};

/**
 * The capability claims of one bearer as a table: stance, ability with its
 * object beneath, condition, mode, grade, maturity and sources.
 *
 * The claims of one stance on one ability are one row, which lists what
 * they state and opens on the claims themselves: each in a sentence, with
 * its means. The rows are grouped under bands naming effect area and
 * function area; a band closes with one line for its abilities that are on
 * no curated list, which opens on their rows. What the bearer enables in a
 * host or requires closes the table in a band of its own. Renders nothing
 * without a claim.
 */
const ClaimTable: React.FC<ClaimTableProps> = ({ claims, print = false }) => {
  const columns = print ? PRINT_COLUMNS : COLUMNS;
  const bands = useMemo(() => groupClaims(claims), [claims]);
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const isOpen = useCallback((key: string) => opened.has(key), [opened]);
  const toggle = useCallback((key: string) => {
    setOpened((current) => {
      const next = new Set(current);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  }, []);

  if (claims.length === 0) {
    return null;
  }

  // The table stands in the frame of every table of the view; a printed
  // page draws it plain
  return (
    <TableContainer
      sx={print ? { overflowX: "auto" } : { ...tableFrame, overflowX: "auto" }}
    >
      <Table
        size="small"
        sx={{
          minWidth: TABLE_MIN_WIDTH,
          // Seven columns in the width of the overlay: the cells stand
          // closer than a table's own, the outer edges where they were
          "& .MuiTableCell-root": { px: "6px" },
          "& .MuiTableCell-root:first-of-type": { pl: 2 },
          "& .MuiTableCell-root:last-of-type": { pr: 1 },
          // A name breaks between its words: a column is never squeezed to
          // the width of a letter
          "& td .MuiLink-root": { overflowWrap: "break-word" },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column} sx={columnHeader}>
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {bands.map((band) => (
            <React.Fragment key={band.key}>
              <TableRow>
                <TableCell colSpan={columns.length} sx={bandCell}>
                  <BandName band={band} />
                </TableCell>
              </TableRow>
              {band.groups.map((group) => (
                <GroupRows
                  key={group.key}
                  group={group}
                  open={isOpen(group.key)}
                  onToggle={toggle}
                  print={print}
                />
              ))}
              {band.unlisted.length > 0 && (
                <UnlistedRows
                  band={band}
                  isOpen={isOpen}
                  onToggle={toggle}
                  print={print}
                />
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ClaimTable;
